import { VIEW_MODE } from '@/common/define/common.define';

export const BASE_MENU_VALUE = {
  HOME: 'home',
  DASHBOARD: 'dashboard',
  ANALYSISBOARD: 'analysisBoard',
  INFRASTRUCTURE: 'infrastructure',
  GPU: 'gpu',
  MESSAGE_QUEUE: 'message-queue',
  KUBERNETES: 'kubernetes',
  CLOUD: 'cloud',
  APPLICATION: 'application',
  DATABASE: 'database',
  BUSINESS: 'business',
  ALERT: 'alert',
  PA: 'pa',
  TOOL: 'tool',
  REPORT: 'report',
  PREDEFINED_REPORT: 'predefined-report',
  AIOPS: 'aiops',
  LOGS: 'logs', // 로그 (2024.02)
  HELP: 'help', // 고객지원 (2024.05)
  SETTINGS: 'myInfo',

  // DB_TYPE 불러오면 순환참조돼서 오류 납니다
  FAULT_ANALYSIS: 'fault-analysis',
  ANALYSIS: 'analysis',
  ORACLE: 'oracle',
  POSTGRESQL: 'postgresql',
  MYSQL: 'mysql',
  SQLSERVER: 'sqlserver',
  REDIS: 'redis',
  CUBRID: 'cubrid',
  MONGODB: 'mongodb',
  TIBERO: 'tibero',
  ALTIBASE: 'altibase',
  CLICKHOUSE: 'clickhouse',
  REDSHIFT: 'redshift',
  SINGLESTORE: 'singlestore',
} as const;

export const SUBTREE_ARRAY = ['children', 'subMenuList'] as const;

// 메뉴 높이 상수 (CSS와 JS에서 공통 사용)
export const MENU_HEIGHT = {
  SUB_MENU_ELEMENT: 36, // 서브 메뉴 요소 높이
  MID_MENU_TITLE: 36, // 중간 메뉴 타이틀 높이
  MID_MENU_BAR: 20, // 중간 메뉴 바 높이 (기본)
  MID_MENU_BAR_LARGE: 24, // 중간 메뉴 바 높이 (1600px 이상)
  MARGIN_BOTTOM: 4, // 메뉴 요소 간 마진
} as const;

export const POPOVER_MENU_TYPES = {
  HELP: {
    value: BASE_MENU_VALUE.HELP,
    selector: '.user-support-popup',
  },
  SETTINGS: {
    value: BASE_MENU_VALUE.SETTINGS,
    selector: '.my-info-popup',
  },
} as const;

type ViewMode = (typeof VIEW_MODE)[keyof typeof VIEW_MODE];

export const VIEW_MODE_SERVICE: Record<ViewMode, string[]> = {
  exemone: ['system', 'k8s', 'application', 'database'],
  maxgauge: ['database'],
  intermax: ['system', 'application'],
  cloudmoa: ['system', 'k8s'],
};

const PA_DB_TYPE = {
  ORACLE: 'oracle',
  POSTGRESQL: 'postgresql',
  MYSQL: 'mysql',
  SQLSERVER: 'sqlserver',
  ALTIBASE: 'altibase',
  TIBERO: 'tibero',
  CLICKHOUSE: 'clickhouse',
  REDSHIFT: 'redshift',
  SINGLESTORE: 'singlestore',
};

export const MAXGAUGE_DATABASE_MENU_KEY_MAPPER = {
  [PA_DB_TYPE.ORACLE]: {
    [`${PA_DB_TYPE.ORACLE}_SQL Analysis`]: {
      menuKey: 'sqlAnalysis',
      menuTextKey: 'WORD.GNB.SQL_ANALYSIS',
    },
    [`${PA_DB_TYPE.ORACLE}_Event Analysis`]: {
      menuKey: 'eventAnalysis',
      menuTextKey: 'WORD.GNB.EVENT_ANALYSIS',
    },
    [`${PA_DB_TYPE.ORACLE}_Session Analysis`]: {
      menuKey: 'sessionAnalysis',
      menuTextKey: 'WORD.GNB.SESSION_ANALYSIS',
    },
    [`${PA_DB_TYPE.ORACLE}_Performance Analysis`]: {
      menuKey: 'performanceAnalysis',
      menuTextKey: 'WORD.GNB.PERFORMANCE_ANALYSIS',
    },
    [`${PA_DB_TYPE.ORACLE}_DataSpace Analysis`]: {
      menuKey: 'dataSpaceAnalysis',
      menuTextKey: 'WORD.GNB.DATASPACE_ANALYSIS',
    },
  },
  [PA_DB_TYPE.POSTGRESQL]: {
    [`${PA_DB_TYPE.POSTGRESQL}_SQL Analysis`]: {
      menuKey: 'sqlAnalysis',
      menuTextKey: 'WORD.GNB.SQL_ANALYSIS',
    },
    [`${PA_DB_TYPE.POSTGRESQL}_Session Analysis`]: {
      menuKey: 'sessionAnalysis',
      menuTextKey: 'WORD.GNB.SESSION_ANALYSIS',
    },
    [`${PA_DB_TYPE.POSTGRESQL}_Performance Analysis`]: {
      menuKey: 'performanceAnalysis',
      menuTextKey: 'WORD.GNB.PERFORMANCE_ANALYSIS',
    },
    [`${PA_DB_TYPE.POSTGRESQL}_DataSpace Analysis`]: {
      menuKey: 'dataSpaceAnalysis',
      menuTextKey: 'WORD.GNB.DATASPACE_ANALYSIS',
    },
  },
  [PA_DB_TYPE.MYSQL]: {
    [`${PA_DB_TYPE.MYSQL}_SQL Analysis`]: {
      menuKey: 'sqlAnalysis',
      menuTextKey: 'WORD.GNB.SQL_ANALYSIS',
    },
    [`${PA_DB_TYPE.MYSQL}_Session Analysis`]: {
      menuKey: 'sessionAnalysis',
      menuTextKey: 'WORD.GNB.SESSION_ANALYSIS',
    },
    [`${PA_DB_TYPE.MYSQL}_Performance Analysis`]: {
      menuKey: 'performanceAnalysis',
      menuTextKey: 'WORD.GNB.PERFORMANCE_ANALYSIS',
    },
    [`${PA_DB_TYPE.MYSQL}_DataSpace Analysis`]: {
      menuKey: 'dataSpaceAnalysis',
      menuTextKey: 'WORD.GNB.DATASPACE_ANALYSIS',
    },
  },
  [PA_DB_TYPE.SQLSERVER]: {
    [`${PA_DB_TYPE.SQLSERVER}_SQL Analysis`]: {
      menuKey: 'sqlAnalysis',
      menuTextKey: 'WORD.GNB.SQL_ANALYSIS',
    },
    [`${PA_DB_TYPE.SQLSERVER}_Session Analysis`]: {
      menuKey: 'sessionAnalysis',
      menuTextKey: 'WORD.GNB.SESSION_ANALYSIS',
    },
    [`${PA_DB_TYPE.SQLSERVER}_Performance Analysis`]: {
      menuKey: 'performanceAnalysis',
      menuTextKey: 'WORD.GNB.PERFORMANCE_ANALYSIS',
    },
    [`${PA_DB_TYPE.SQLSERVER}_DataSpace Analysis`]: {
      menuKey: 'dataSpaceAnalysis',
      menuTextKey: 'WORD.GNB.DATASPACE_ANALYSIS',
    },
  },
  [PA_DB_TYPE.ALTIBASE]: {
    [`${PA_DB_TYPE.ALTIBASE}_SQL Analysis`]: {
      menuKey: 'sqlAnalysis',
      menuTextKey: 'WORD.GNB.SQL_ANALYSIS',
    },
    [`${PA_DB_TYPE.ALTIBASE}_Performance Analysis`]: {
      menuKey: 'performanceAnalysis',
      menuTextKey: 'WORD.GNB.PERFORMANCE_ANALYSIS',
    },
  },
  [PA_DB_TYPE.TIBERO]: {
    [`${PA_DB_TYPE.TIBERO}_SQL Analysis`]: {
      menuKey: 'sqlAnalysis',
      menuTextKey: 'WORD.GNB.SQL_ANALYSIS',
    },
    [`${PA_DB_TYPE.TIBERO}_Session Analysis`]: {
      menuKey: 'sessionAnalysis',
      menuTextKey: 'WORD.GNB.SESSION_ANALYSIS',
    },
    [`${PA_DB_TYPE.TIBERO}_Performance Analysis`]: {
      menuKey: 'performanceAnalysis',
      menuTextKey: 'WORD.GNB.PERFORMANCE_ANALYSIS',
    },
    [`${PA_DB_TYPE.TIBERO}_DataSpace Analysis`]: {
      menuKey: 'dataSpaceAnalysis',
      menuTextKey: 'WORD.GNB.DATASPACE_ANALYSIS',
    },
  },
  [PA_DB_TYPE.CLICKHOUSE]: {
    [`${PA_DB_TYPE.CLICKHOUSE}_SQL Analysis`]: {
      menuKey: 'sqlAnalysis',
      menuTextKey: 'WORD.GNB.SQL_ANALYSIS',
    },
    [`${PA_DB_TYPE.CLICKHOUSE}_Performance Analysis`]: {
      menuKey: 'performanceAnalysis',
      menuTextKey: 'WORD.GNB.PERFORMANCE_ANALYSIS',
    },
  },
  [PA_DB_TYPE.REDSHIFT]: {
    [`${PA_DB_TYPE.REDSHIFT}_SQL Analysis`]: {
      menuKey: 'sqlAnalysis',
      menuTextKey: 'WORD.GNB.SQL_ANALYSIS',
    },
    [`${PA_DB_TYPE.REDSHIFT}_Performance Analysis`]: {
      menuKey: 'performanceAnalysis',
      menuTextKey: 'WORD.GNB.PERFORMANCE_ANALYSIS',
    },
  },
  [PA_DB_TYPE.SINGLESTORE]: {
    [`${PA_DB_TYPE.SINGLESTORE}_SQL Analysis`]: {
      menuKey: 'sqlAnalysis',
      menuTextKey: 'WORD.GNB.SQL_ANALYSIS',
    },
    [`${PA_DB_TYPE.SINGLESTORE}_Performance Analysis`]: {
      menuKey: 'performanceAnalysis',
      menuTextKey: 'WORD.GNB.PERFORMANCE_ANALYSIS',
    },
  },
} as const;
